/**
 * Capsize wrapper used by both renderers (DOM + Pixi) to compute the
 * trim values that make the visual cap-height box land at a known
 * pixel rectangle. Apply the same trim math on both sides and the two
 * renderers centre text identically — no more hand-tuned offsets.
 *
 * See `docs/dom-pixi-parity-stack.md` (rationale) and
 * `docs/dom-pixi-parity-plan.md` Sprint 1 (this code).
 */

import { precomputeValues, createStyleObject } from "@capsizecss/core";
import type { FontMetrics } from "@capsizecss/core";

import { resolveFontMetrics } from "./font-metrics";

export interface CapsizeTrim {
  /** Effective fontSize in px after the cap-height transformation. */
  fontSizePx: number;
  /** Effective lineHeight in px after the cap-height transformation. */
  lineHeightPx: number;
  /** CSS `padding-top` (negative px) to align the cap-height box top. */
  capHeightTrim: number;
  /** CSS `padding-bottom` (negative px) to align the baseline. */
  baselineTrim: number;
}

const parsePx = (s: string): number => parseFloat(s);

/**
 * Run Capsize's `precomputeValues` and return the pixel-numeric form
 * (Capsize emits CSS strings like "1.234em"; we convert to px so both
 * renderers can apply them uniformly).
 *
 * @param fontMetrics  Pre-computed metrics for the font family. Get
 *                     these from `resolveFontMetrics(fontFamily)`.
 * @param fontSize     Designed font size in px (matches the seed's
 *                     `fontSize` value).
 * @param lineHeight   Line-height in px. Convention across the
 *                     codebase: `fontSize * 1.2` — locked the same on
 *                     both renderers so trim values match.
 */
export const getCapsizeTrim = (
  fontMetrics: FontMetrics,
  fontSize: number,
  lineHeight: number,
): CapsizeTrim => {
  const v = precomputeValues({ fontMetrics, fontSize, leading: lineHeight });
  return {
    fontSizePx: parsePx(v.fontSize),
    lineHeightPx: parsePx(v.lineHeight),
    capHeightTrim: parsePx(v.capHeightTrim),
    baselineTrim: parsePx(v.baselineTrim),
  };
};

/**
 * Same call but returns the CSS style object Capsize generates —
 * apply directly to a `<style>` prop in DOM to mirror the trim values
 * on the CSS side. Keeps both renderers in lockstep without manually
 * re-deriving padding values from the trim numbers.
 */
export const getCapsizeStyle = (
  fontMetrics: FontMetrics,
  fontSize: number,
  lineHeight: number,
): ReturnType<typeof createStyleObject> =>
  createStyleObject({ fontMetrics, fontSize, leading: lineHeight });

/**
 * Convenience: resolve metrics from a CSS `font-family` string and
 * compute trim values in one call. Returns `null` if the font isn't
 * in the metrics map — caller should fall back to legacy centring.
 */
export const getCapsizeTrimForFamily = (
  fontFamily: string | undefined,
  fontSize: number,
  lineHeight: number,
): CapsizeTrim | null => {
  const metrics = resolveFontMetrics(fontFamily);
  if (!metrics) return null;
  return getCapsizeTrim(metrics, fontSize, lineHeight);
};

/**
 * CSS includes trailing letter spacing in its line box; Pixi v8 measures
 * only the gaps between glyphs. Shift centred/right-aligned Pixi text left
 * by the anchored share of that extra CSS gap to match glyph positions.
 * A positive pivot shifts the glyphs without changing layout coordinates.
 * Call once after constructing the Text and setting its anchor.
 */
export const applyPixiLetterSpacingCompensation = (
  text: { pivot: { x: number }; anchor: { x: number } },
  letterSpacing: number,
): void => {
  if (!letterSpacing || letterSpacing === 0) return;
  text.pivot.x += letterSpacing * text.anchor.x;
};
